import React, { useEffect, useState } from "react";
import { insightsApi } from "./api";
import { formatErr } from "../api";
import { toast } from "sonner";
import { Plus, ShieldCheck, EyeOff, User, X, ChevronDown, ChevronRight } from "lucide-react";
import { tokenStore } from "../api";

const VIS = {
  public: { label: "Public", cls: "bg-[#3F6250]/10 text-[#3F6250]" },
  doctor_portal: { label: "Doctor Portal", cls: "bg-clinical text-navy" },
  private_admin: { label: "Private · Admin only", cls: "bg-[#9F1239]/10 text-[#9F1239]" },
};
const STATUS = {
  draft: { label: "Draft", cls: "bg-line text-stone2" },
  pending_approval: { label: "Pending approval", cls: "bg-[#D97706]/15 text-[#D97706]" },
  approved: { label: "Approved", cls: "bg-prestige-bg text-prestige" },
  archived: { label: "Archived", cls: "bg-line text-stone2" },
};

export default function ClinicalInsights({ host, hostId }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const me = tokenStore.user;
  const canApprove = ["super_administrator","medical_reviewer"].includes(me?.role);

  const load = () => insightsApi.listOn(host, hostId).then(setRows).catch(()=>{});
  useEffect(() => { if (hostId) load(); /* eslint-disable-next-line */ }, [hostId]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };

  return (
    <div data-testid="insights-panel" className="border border-line bg-white">
      <div className="flex items-center justify-between px-5 py-3 border-b border-line">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} strokeWidth={1.5} className="text-prestige"/>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">Dr. Tania's Clinical Insights</div>
        </div>
        <button data-testid="new-insight-btn" onClick={()=>setShowForm(v=>!v)} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta inline-flex items-center gap-1">
          {showForm ? <><X size={12}/> Cancel</> : <><Plus size={12}/> New</>}
        </button>
      </div>
      {showForm && <InsightForm host={host} hostId={hostId} onSaved={() => { setShowForm(false); load(); }}/>}
      <div className="divide-y divide-line">
        {rows.map(i => {
          const isOpen = expanded === i.id;
          return (
            <div key={i.id} data-testid={`ins-${i.id}`} className="p-5">
              <button onClick={()=>setExpanded(isOpen ? null : i.id)} className="w-full text-left flex items-start gap-3">
                {isOpen ? <ChevronDown size={16} className="mt-1 text-stone2 shrink-0"/> : <ChevronRight size={16} className="mt-1 text-stone2 shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif text-lg text-navy leading-tight">{i.title}</div>
                    <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${VIS[i.visibility]?.cls}`}>{VIS[i.visibility]?.label}</span>
                    <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${STATUS[i.status]?.cls}`}>{STATUS[i.status]?.label}</span>
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-1">{i.variant.replace(/_/g,' ')}</div>
                </div>
              </button>
              {isOpen && (
                <div className="pl-7 mt-4 space-y-4">
                  {i.visibility === "private_admin" && (
                    <div className="text-xs text-[#9F1239] bg-[#9F1239]/8 border border-[#9F1239]/30 px-3 py-2 flex items-center gap-2">
                      <EyeOff size={12} strokeWidth={1.5}/> Confidential — never shown to patients or doctors
                    </div>
                  )}
                  <div className="prose-article text-sm" dangerouslySetInnerHTML={{__html: (i.body_markdown||"").replace(/\n/g,"<br>")}}/>
                  {i.approved_by_user_id && <div className="mono text-[10px] text-stone2">Approved · {(i.approved_at||"").slice(0,16).replace('T',' ')}</div>}
                  <div className="flex flex-wrap gap-2">
                    {i.status === "draft" && (
                      <button data-testid={`submit-${i.id}`} onClick={()=>act(()=>insightsApi.submitForApproval(host, hostId, i.id), "Submitted for approval")}
                        className="mono text-[10px] uppercase tracking-widest border border-navy text-navy px-3 py-1.5 hover:bg-navy hover:text-white">Submit for approval</button>
                    )}
                    {i.status === "pending_approval" && canApprove && (
                      <button data-testid={`approve-${i.id}`} onClick={()=>act(()=>insightsApi.approve(host, hostId, i.id), "Insight approved")}
                        className="mono text-[10px] uppercase tracking-widest bg-prestige text-white px-3 py-1.5 hover:bg-prestige/85">Approve</button>
                    )}
                    {i.visibility !== "public" && i.status === "draft" && canApprove && (
                      <button data-testid={`fast-approve-${i.id}`} onClick={()=>act(()=>insightsApi.approve(host, hostId, i.id), "Insight approved")}
                        className="mono text-[10px] uppercase tracking-widest bg-prestige text-white px-3 py-1.5 hover:bg-prestige/85">Approve (non-public)</button>
                    )}
                    {i.status !== "archived" && (
                      <button data-testid={`archive-${i.id}`} onClick={()=>act(()=>insightsApi.archive(host, hostId, i.id), "Archived")}
                        className="mono text-[10px] uppercase tracking-widest border border-line text-stone2 px-3 py-1.5 hover:text-navy hover:border-navy">Archive</button>
                    )}
                    <button data-testid={`del-${i.id}`} onClick={()=>{if(window.confirm("Delete this insight?")) act(()=>insightsApi.remove(host, hostId, i.id), "Deleted")}}
                      className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline ml-auto">Delete</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && !showForm && <div className="p-5 text-sm text-stone2">No insights yet. Add Dr. Tania's own reasoning for this entity.</div>}
      </div>
    </div>
  );
}

function InsightForm({ host, hostId, onSaved }) {
  const [f, setF] = useState({ visibility: "public", variant: "clinical_pearl", title: "", body_markdown: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await insightsApi.create(host, hostId, f); toast.success("Insight saved as draft"); onSaved(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-5 space-y-3 border-b border-line bg-clinical/30" data-testid="new-insight-form">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Visibility">
          <select data-testid="ins-visibility" value={f.visibility} onChange={e=>set("visibility", e.target.value)} className={inputCls}>
            <option value="public">Public — patients & everyone</option>
            <option value="doctor_portal">Doctor Portal — verified doctors only</option>
            <option value="private_admin">Private · Admin only — confidential</option>
          </select>
        </Field>
        <Field label="Variant">
          <select data-testid="ins-variant" value={f.variant} onChange={e=>set("variant", e.target.value)} className={inputCls}>
            <option value="clinical_pearl">Clinical Pearl</option>
            <option value="prevention_note">Prevention Note</option>
            <option value="pitfall_to_avoid">Pitfall to Avoid</option>
            <option value="when_to_refer">When to Refer</option>
            <option value="decision_point">Decision Point</option>
            <option value="patient_conversation">Patient Conversation</option>
          </select>
        </Field>
      </div>
      <Field label="Title">
        <input data-testid="ins-title" required value={f.title} onChange={e=>set("title", e.target.value)} className={inputCls}/>
      </Field>
      <Field label="Body (markdown)">
        <textarea data-testid="ins-body" required rows={5} value={f.body_markdown} onChange={e=>set("body_markdown", e.target.value)} className={`${inputCls} font-mono text-xs`}/>
      </Field>
      {f.visibility === "private_admin" && (
        <div className="text-xs text-[#9F1239] flex items-center gap-2"><EyeOff size={12}/> This will only ever be visible in this admin panel.</div>
      )}
      <div className="flex justify-end">
        <button data-testid="ins-save" type="submit" disabled={saving} className="bg-navy hover:bg-navy-hover text-white text-sm px-5 py-2 disabled:opacity-60">{saving ? "Saving…" : "Save as Draft"}</button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (<label className="block"><div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1">{label}</div>{children}</label>);
}
const inputCls = "w-full bg-white border border-line px-3 py-2 text-sm focus:outline-none focus:border-navy";
